import { PROJECT_COLORS } from './types';

/**
 * Project colours beyond the eight hues of the palette (round 5, request 18): a colour code "#rrggbb" chosen with the
 * colour field of the browser (RGB) or typed in. "Farben vergeben" keeps every colour that active projects have and
 * gives the projects without one the colour that differs most from all of them (largest smallest distance in OKLab,
 * a space in which distances follow the perceived difference).
 */

/** Light values of the palette (styles.css, --pc-*): the reference for distances. */
export const PROJECT_COLOR_HEX: Record<(typeof PROJECT_COLORS)[number], string> = {
  blue: '#2a78d6',
  orange: '#eb6834',
  aqua: '#1baf7a',
  yellow: '#eda100',
  magenta: '#e87ba4',
  green: '#008300',
  violet: '#6250d6',
  red: '#e34948',
};

const HEX = /^#[0-9a-f]{6}$/;

export function isHexColor(c: unknown): c is string {
  return typeof c === 'string' && HEX.test(c.toLowerCase());
}

/** A colour of a project: a hue of the palette or a colour code. */
export function isValidProjectColor(c: unknown): boolean {
  return (typeof c === 'string' && (PROJECT_COLORS as readonly string[]).includes(c)) || isHexColor(c);
}

/** The colour code of a stored colour (palette name or code), or null. */
export function projectColorHex(c: string | null | undefined): string | null {
  if (!c) return null;
  if ((PROJECT_COLORS as readonly string[]).includes(c)) return PROJECT_COLOR_HEX[c as keyof typeof PROJECT_COLOR_HEX];
  return isHexColor(c) ? c.toLowerCase() : null;
}

type Lab = [number, number, number];

function srgbToLinear(v: number) {
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(v: number) {
  const x = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.min(1, Math.max(0, x));
}

/** OKLab of a colour code (Björn Ottosson, 2020). */
export function hexToOklab(hex: string): Lab {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => srgbToLinear(v / 255));
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}

function oklchToHex(L: number, C: number, hDeg: number): string {
  const h = (hDeg * Math.PI) / 180;
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const rgb = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
  return `#${rgb.map((v) => Math.round(linearToSrgb(v) * 255).toString(16).padStart(2, '0')).join('')}`;
}

export function colorDistance(a: string, b: string): number {
  const x = hexToOklab(a);
  const y = hexToOklab(b);
  return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
}

/**
 * Candidates for a new colour: the palette first (tuned for light and dark mode), then 48 hues in two lightness steps
 * with a chroma that stays readable as a dot and a bar on white and on dark grey.
 */
const CANDIDATES: string[] = (() => {
  const out: string[] = [...PROJECT_COLORS];
  for (const L of [0.58, 0.7]) for (let h = 0; h < 360; h += 7.5) out.push(oklchToHex(L, 0.15, h));
  return out;
})();

/**
 * The colour that differs most from the colours in use (largest smallest distance); at equal distance a hue of the
 * palette. With nothing in use: the first hue of the palette.
 */
export function mostDistinctColor(used: readonly (string | null | undefined)[]): string {
  const taken = used.map(projectColorHex).filter((x): x is string => !!x);
  if (!taken.length) return PROJECT_COLORS[0];
  const score = (c: string) => Math.min(...taken.map((u) => colorDistance(projectColorHex(c)!, u)));
  const best = (list: readonly string[]) => list.reduce<{ c: string; s: number }>((b, c) => (score(c) > b.s + 1e-9 ? { c, s: score(c) } : b), { c: list[0], s: -1 });
  const palette = best(PROJECT_COLORS.filter((c) => !taken.includes(PROJECT_COLOR_HEX[c])));
  const any = best(CANDIDATES);
  // a free hue of the palette (tuned for light and dark mode) when it is nearly as far as the best code
  return palette.s >= 0.85 * any.s ? palette.c : any.c;
}
